import { useCallback } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';

import {
  createApplicationConfiguration,
  deleteApplicationConfiguration,
  fetchApplicationConfigurations,
  saveApplicationConfiguration,
} from '@/domains/application/applicationApi';

interface UseApplicationConfigurationsOptions {
  bitId: string;
  onCreateError?: (error: unknown) => void;
  onCreateSuccess?: () => void | Promise<void>;
  onDeleteError?: (error: unknown) => void;
  onDeleteSuccess?: () => void | Promise<void>;
  onSaveError?: (error: unknown) => void;
  onSaveSuccess?: () => void | Promise<void>;
}

export const getApplicationConfigurationsQueryKey = (bitId: string) =>
  ['application-configurations', bitId] as const;

export const useApplicationConfigurations = ({
  bitId,
  onCreateError,
  onCreateSuccess,
  onDeleteError,
  onDeleteSuccess,
  onSaveError,
  onSaveSuccess,
}: UseApplicationConfigurationsOptions) => {
  const query = useQuery({
    queryKey: getApplicationConfigurationsQueryKey(bitId),
    queryFn: () => fetchApplicationConfigurations(bitId),
    enabled: !!bitId,
    retry: false,
  });

  const refetchApplicationConfigurations = query.refetch;

  const handleMutationSuccess = useCallback(
    async (callback?: () => void | Promise<void>) => {
      await refetchApplicationConfigurations();
      await callback?.();
    },
    [refetchApplicationConfigurations],
  );

  const {
    mutate: createApplicationConfigurationMutation,
    isPending: isCreatingConfiguration,
  } = useMutation({
    mutationFn: createApplicationConfiguration,
    onSuccess: () => handleMutationSuccess(onCreateSuccess),
    onError: onCreateError,
  });

  const {
    mutate: saveApplicationConfigurationMutation,
    isPending: isSavingConfiguration,
  } = useMutation({
    mutationFn: saveApplicationConfiguration,
    onSuccess: () => handleMutationSuccess(onSaveSuccess),
    onError: onSaveError,
  });

  const {
    mutate: deleteApplicationConfigurationMutation,
    isPending: isDeletingConfiguration,
  } = useMutation({
    mutationFn: deleteApplicationConfiguration,
    onSuccess: () => handleMutationSuccess(onDeleteSuccess),
    onError: onDeleteError,
  });

  return {
    applicationConfigurations: query.data,
    applicationConfigurationsError: query.error,
    createApplicationConfiguration:
      createApplicationConfigurationMutation,
    deleteApplicationConfiguration:
      deleteApplicationConfigurationMutation,
    isCreatingConfiguration,
    isDeletingConfiguration,
    isFetchingApplicationConfigurations: query.isFetching,
    isSavingConfiguration,
    refetchApplicationConfigurations,
    saveApplicationConfiguration: saveApplicationConfigurationMutation,
  };
};
